import { MutableRefObject, useEffect } from 'react';

import { Editor } from '@tiptap/react';

interface UseEditorEventsProps {
  editor: Editor | null;
  editorContentRef: MutableRefObject<HTMLDivElement | null>;
}

export function useRegisterEditorEvents({ editor, editorContentRef }: UseEditorEventsProps) {
  // Add keyboard event handler for deleting selected images
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Check if we have a selected node
        const { selection } = editor.state;
        if (selection && editor.isActive('image')) {
          // Delete the selected image
          editor.commands.deleteSelection();
          // Prevent default behavior
          event.preventDefault();
        }
      }
    };

    // Handle paste events for images
    const handlePaste = (_event: ClipboardEvent) => {
      // Apply classes/attributes to pasted images
      setTimeout(() => {
        // Find any newly pasted images that might not have proper classes
        const editorElement = editorContentRef.current;
        const images = editorElement?.querySelectorAll('.ProseMirror img:not(.selectable-image)');
        if (images && images.length) {
          images.forEach((img) => {
            img.classList.add('selectable-image');
            img.setAttribute('draggable', 'true');
          });
        }
      }, 100); // Small delay to ensure content has been inserted
    };

    // Add event listeners to the editor DOM element
    const editorElement = editorContentRef.current;
    if (editorElement) {
      editorElement.addEventListener('keydown', handleKeyDown);
      editorElement.addEventListener('paste', handlePaste);
    }

    // Cleanup
    return () => {
      if (editorElement) {
        editorElement.removeEventListener('keydown', handleKeyDown);
        editorElement.removeEventListener('paste', handlePaste);
      }
    };
  }, [editor, editorContentRef]);
}
